import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

// Curator-only modal for adding documents to the company's Document Library.
// Takes any number of files (drop or pick), a title per file and a folder.
// Accepted types mirror backend/companies/library/libs/validation.js exactly:
// the legacy .doc/.ppt formats the one-off Document Defense modal still lists
// cannot be parsed server-side, so they are deliberately absent here.
export const ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.pptx', '.txt', '.md'];
export const MAX_FILE_SIZE = 20 * 1024 * 1024;

const isAcceptedFile = (file) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    return ext ? ACCEPTED_EXTENSIONS.includes(`.${ext}`) : false;
};

const titleFor = (file) => file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

const problemWith = (file) => {
    if (!isAcceptedFile(file)) return `Unsupported file type. Use ${ACCEPTED_EXTENSIONS.join(', ')}.`;
    if (file.size > MAX_FILE_SIZE) return 'Over 20 MB.';
    return null;
};

const fillKeyframes = `
@keyframes multiplyFillLib {
    0% { background-position: 100% 0; }
    100% { background-position: 0% 0; }
}
`;

const font = { fontFamily: 'Montserrat' };
const UPLOAD_LABELS = {
    waiting: 'Waiting',
    uploading: 'Uploading',
    reading: 'Added. Murphy is reading it',
    error: 'Could not add',
};

let nextKey = 1;

const AddDocumentModal = ({
    open, onClose, onAdd, busy, uploads = [], uploadsPending = 0, folders = [], initialFolderId = null, companyName,
}) => {
    const [rows, setRows] = useState([]);
    const [folderId, setFolderId] = useState(initialFolderId || '');
    const [error, setError] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (!open) {
            setRows([]);
            setError('');
            setDragOver(false);
            setSubmitted(false);
        } else {
            setFolderId(initialFolderId || '');
        }
    }, [open, initialFolderId]);

    if (!open) return null;

    const addFiles = (fileList) => {
        setError('');
        const picked = Array.from(fileList || []);
        if (picked.length === 0) return;
        setRows((prev) => [
            ...prev,
            ...picked.map((file) => ({ key: `f${nextKey++}`, file, title: titleFor(file), problem: problemWith(file) })),
        ]);
    };

    const removeRow = (key) => setRows((prev) => prev.filter((r) => r.key !== key));
    const setTitle = (key, title) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, title } : r)));

    const valid = rows.filter((r) => !r.problem);

    const submit = async () => {
        if (valid.length === 0 || busy) return;
        setError('');
        setSubmitted(true);
        try {
            await onAdd(valid.map(({ key, file, title }) => ({ key, file, title })), folderId || null);
        } catch (e) {
            setError(e?.message || 'Something went wrong. Please try again.');
        }
    };

    // Once every file has been handed to the server (or failed), the dialog can
    // close; Murphy keeps reading in the background and the list shows progress.
    const showQueue = submitted && uploads.length > 0;
    const canClose = !busy || (showQueue && uploadsPending === 0);
    const added = uploads.filter((u) => u.status === 'reading').length;
    const failed = uploads.filter((u) => u.status === 'error').length;

    return createPortal(
        <div
            onClick={canClose ? onClose : undefined}
            style={{
                position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000,
            }}
        >
            <style>{fillKeyframes}</style>
            <div
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-label="Add documents to the library"
                style={{
                    background: '#fff', borderRadius: 10, width: '92%', maxWidth: 620, maxHeight: '90vh', overflow: 'auto',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                }}
            >
                <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ ...font, margin: 0, fontSize: 18, fontWeight: 700, color: '#1a2b3d' }}>Add to the Document Library</h3>
                    {canClose && (
                        <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#64748b' }}>&times;</button>
                    )}
                </div>

                <div style={{ padding: 24 }}>
                    {showQueue ? (
                        <div>
                            <div style={{ textAlign: 'center', padding: '6px 10px 18px' }}>
                                <div style={{
                                    ...font, fontSize: 36, fontWeight: 800, lineHeight: 1.4, letterSpacing: -1, padding: '4px 0',
                                    background: 'linear-gradient(to right, #0784a7 50%, #d0d0d0 50%)', backgroundSize: '200% 100%',
                                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                                    animation: uploadsPending > 0 ? 'multiplyFillLib 2.5s ease-in-out infinite alternate' : 'none', userSelect: 'none',
                                }}>
                                    Multiply
                                </div>
                                <p style={{ ...font, fontSize: 14, color: '#475569', margin: 0 }} data-testid="library-upload-summary">
                                    {uploadsPending > 0
                                        ? `Uploading ${uploads.length === 1 ? 'the document' : `${uploads.length} documents`}: ${added} handed to Murphy so far`
                                        : `${added} ${added === 1 ? 'document' : 'documents'} handed to Murphy${failed ? `, ${failed} could not be added` : ''}.`}
                                </p>
                                <p style={{ ...font, fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
                                    Murphy reads a few documents at a time and prepares a bank of questions for each. You can close this and watch progress in the list.
                                </p>
                            </div>
                            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }} aria-label="Upload progress">
                                {uploads.map((u) => (
                                    <li key={u.key} style={{ ...font, display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, padding: '8px 10px', background: '#f8fafc', borderRadius: 6 }}>
                                        <span style={{ color: '#1a2b3d', minWidth: 0, overflowWrap: 'anywhere' }}>{u.title || u.fileName}</span>
                                        <span style={{ color: u.status === 'error' ? '#b91c1c' : '#475569', whiteSpace: 'nowrap' }}>
                                            {UPLOAD_LABELS[u.status] || u.status}{u.status === 'error' && u.error ? `: ${u.error}` : ''}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    disabled={!canClose}
                                    style={{ ...font, background: '#0784a7', color: '#fff', border: 'none', borderRadius: 6, padding: '9px 18px', fontSize: 14, fontWeight: 600, cursor: canClose ? 'pointer' : 'not-allowed', opacity: canClose ? 1 : 0.5 }}
                                >
                                    Done
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div
                                onClick={() => inputRef.current?.click()}
                                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                                style={{
                                    border: `2px dashed ${dragOver ? '#0784a7' : '#cbd5e1'}`, borderRadius: 8, padding: '24px 16px',
                                    textAlign: 'center', cursor: 'pointer', background: dragOver ? 'rgba(7, 132, 167, 0.05)' : '#f8fafc',
                                }}
                            >
                                <input
                                    ref={inputRef}
                                    type="file"
                                    multiple
                                    accept={ACCEPTED_EXTENSIONS.join(',')}
                                    style={{ display: 'none' }}
                                    onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                                    data-testid="library-file-input"
                                />
                                <div style={{ ...font, fontSize: 14, color: '#475569' }}>
                                    {rows.length ? 'Drop more files here or click to add more' : 'Drop files here or click to choose'}
                                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>PDF, Word, PowerPoint, text or Markdown, up to 20 MB each. Add as many as you like.</div>
                                </div>
                            </div>

                            {rows.length > 0 && (
                                <ul style={{ listStyle: 'none', margin: '14px 0 0', padding: 0, display: 'grid', gap: 8 }} aria-label="Files to add">
                                    {rows.map((r) => (
                                        <li key={r.key} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, alignItems: 'start', padding: '8px 10px', background: '#f8fafc', borderRadius: 6 }}>
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ ...font, fontSize: 12, color: '#64748b', overflowWrap: 'anywhere' }}>{r.file.name}</div>
                                                {r.problem ? (
                                                    <div style={{ ...font, fontSize: 12, color: '#b91c1c', marginTop: 4 }}>{r.problem}</div>
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={r.title}
                                                        maxLength={200}
                                                        aria-label={`Title for ${r.file.name}`}
                                                        onChange={(e) => setTitle(r.key, e.target.value)}
                                                        style={{ ...font, display: 'block', width: '100%', marginTop: 4, padding: '7px 10px', fontSize: 13, border: '1.5px solid #cbd5e1', borderRadius: 6, boxSizing: 'border-box' }}
                                                    />
                                                )}
                                            </div>
                                            <button type="button" onClick={() => removeRow(r.key)} aria-label={`Remove ${r.file.name}`} style={{ ...font, background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>&times;</button>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <label style={{ ...font, display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', margin: '16px 0 6px' }}>
                                Folder
                                <select
                                    value={folderId}
                                    onChange={(e) => setFolderId(e.target.value)}
                                    aria-label="Folder"
                                    style={{ ...font, display: 'block', width: '100%', marginTop: 6, padding: '8px 10px', fontSize: 14, border: '1.5px solid #cbd5e1', borderRadius: 6, background: '#fff' }}
                                >
                                    <option value="">Unfiled</option>
                                    {folders.map((f) => <option key={f.folderId} value={f.folderId}>{f.name}</option>)}
                                </select>
                            </label>

                            <div style={{ background: '#f1f5f9', borderRadius: 6, padding: '12px 14px', marginTop: 16 }}>
                                <p style={{ ...font, fontSize: 12, fontWeight: 600, color: '#475569', margin: '0 0 6px' }}>How your data is handled</p>
                                <p style={{ ...font, fontSize: 11, color: '#64748b', margin: 0, lineHeight: 1.6 }}>
                                    Library documents are kept until a curator removes them and are visible to everyone in {companyName || 'your company'}.
                                    Murphy reads each file to prepare practice questions; the questions and model answers are never shown to reps.
                                    Our AI model does not retain, learn from, or store uploaded content.{' '}
                                    <span style={{ color: '#dc2626', fontWeight: 600 }}>Do not upload sensitive or personally identifiable information.</span>
                                </p>
                            </div>

                            {error && (
                                <div style={{ ...font, marginTop: 14, padding: '10px 12px', background: 'rgba(220, 38, 38, 0.08)', color: '#b91c1c', borderRadius: 6, fontSize: 13 }}>
                                    {error}
                                </div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                                <button type="button" onClick={onClose} style={{ ...font, background: 'transparent', border: '1.5px solid #cbd5e1', borderRadius: 6, padding: '9px 16px', fontSize: 14, cursor: 'pointer', color: '#475569' }}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={submit}
                                    disabled={valid.length === 0}
                                    style={{ ...font, background: '#0784a7', color: '#fff', border: 'none', borderRadius: 6, padding: '9px 18px', fontSize: 14, fontWeight: 600, cursor: valid.length ? 'pointer' : 'not-allowed', opacity: valid.length ? 1 : 0.5 }}
                                >
                                    {valid.length > 1 ? `Add ${valid.length} documents` : 'Add to library'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default AddDocumentModal;
